import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { isApiError, normalizeError } from './api/errors';
import { toastApiError } from './api/error-toast';
import { captureQueryError } from './monitoring';
import { auditLogKeys } from './queryKeys';

/**
 * The default read-retry policy. Exported so a query that knows one of its
 * failures is permanent can decline that case and still defer to this for
 * everything else, rather than restating the policy and drifting from it.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (isApiError(error)) {
    // 400 joined this list on 2026-08-29. A validation rejection is a verdict:
    // the server read the request and refused it, so the identical request
    // gets the identical answer and the retries are pure waste. Staging showed
    // the cost — a `windowDays` the API did not yet know was refused, retried
    // twice, and then re-refused on every 30s poll, three requests a minute
    // for as long as the tab stayed open.
    //
    // Here rather than on the one hook that noticed: nothing anywhere gains
    // from re-sending a request the server has already read and rejected.
    const noRetryStatuses = [400, 401, 403, 404];
    if (noRetryStatuses.includes(error.status)) return false;
  }
  return true;
}

/**
 * What every failed READ does: report it, then decide whether the person sees
 * it. Exported so the queries that rely on being silent can prove it against
 * the real handler rather than a stand-in that drifts from it.
 */
export function handleQueryError(
  error: unknown,
  query: { queryKey: readonly unknown[]; meta?: Record<string, unknown> },
): void {
  // Before the suppression check on purpose: a query that shows the person
  // nothing still belongs in Sentry / RUM, which is where a background service
  // going down has to be visible.
  captureQueryError(error, query.queryKey);

  if (query.meta?.suppressToast) return;
  const apiError = isApiError(error) ? error : normalizeError(error);

  if (import.meta.env.DEV) {
    console.error('[QueryCache error]', apiError);
  }

  // A query is a READ. A 403 means the user simply lacks `view` for this
  // data — they took no action, so a toast is noise, and it repeats on any
  // polling/refetch (e.g. "Required permission(s): billing:view" every few
  // seconds). Silently skip it: the component renders its empty/hidden state
  // instead. Permission errors on WRITES surface through mutation handlers,
  // and other read failures (network, 5xx, etc.) still toast.
  if (apiError.status === 403) return;

  toastApiError(apiError);
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  // Any successful mutation invalidates all audit log queries. Audit data
  // is cached per-entity with staleTime: Infinity for efficiency — this
  // hook ensures the UI reflects newly-captured audit rows as soon as
  // the mutation that created them completes.
  mutationCache: new MutationCache({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditLogKeys.all() });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 300_000,
      retry: shouldRetry,
    },
    mutations: {
      retry: false,
    },
  },
});
